import React, { useState } from 'react';

/**
 * Tableau par catégories dépliables — replié par défaut (juste le chiffre global) pour
 * privilégier la lisibilité ; on déplie pour décocher ou modifier des points précis, ou pour
 * ajouter un nouvel usage manuel directement dans la catégorie concernée (pas de sélecteur de
 * catégorie séparé). Utilisé par SourcesStep (gisement) et UsagesStep (débouchés).
 */
const GroupedUsageTable = ({ items, setItems, groups }) => {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpand = (categoryId) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(categoryId)) next.delete(categoryId); else next.add(categoryId);
    return next;
  });

  const updateItem = (id, patch) => setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id) => setItems(prev => prev.filter(it => it.id !== id));

  const toggleCategoryAll = (categoryId, checked) => setItems(prev => prev.map(it => (
    it.categoryId === categoryId ? { ...it, enabled: checked } : it
  )));

  const addItem = (group) => {
    setItems(prev => [...prev, {
      id: `custom-${Date.now()}`,
      categoryId: group.id,
      categoryName: group.name,
      name: `Nouvel élément (${group.name})`,
      volume: 1,
      enabled: true,
      fromAudit: false,
    }]);
    setExpanded(prev => new Set(prev).add(group.id));
  };

  return (
    <div className="gw-grouped-table">
      {groups.map(group => {
        const catItems = items.filter(it => it.categoryId === group.id);
        const total = catItems.filter(it => it.enabled).reduce((sum, it) => sum + (it.volume || 0), 0);
        const allChecked = catItems.length > 0 && catItems.every(it => it.enabled);
        const isExpanded = expanded.has(group.id);

        return (
          <div className="gw-category" key={group.id}>
            <button type="button" className="gw-category-header" onClick={() => toggleExpand(group.id)}>
              <span className={`gw-expand-icon ${isExpanded ? 'gw-expand-icon--open' : ''}`}>▸</span>
              {catItems.length > 0 && (
                <input
                  type="checkbox"
                  checked={allChecked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => toggleCategoryAll(group.id, e.target.checked)}
                />
              )}
              <span className="gw-category-name">{group.name}</span>
              <span className="gw-category-count">
                {catItems.length} point{catItems.length !== 1 ? 's' : ''}
              </span>
              <span className="gw-category-total">
                {total.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³/an
              </span>
            </button>

            {isExpanded && (
              <div className="gw-item-list">
                {catItems.length === 0 && (
                  <div className="gw-category-empty">Aucun équipement de ce type détecté dans l'audit de ce bâtiment.</div>
                )}
                {catItems.map(it => (
                  <div className="gw-item-row" key={it.id}>
                    <input
                      type="checkbox"
                      checked={it.enabled}
                      onChange={(e) => updateItem(it.id, { enabled: e.target.checked })}
                    />
                    <input
                      type="text"
                      className="gw-item-name"
                      value={it.name}
                      disabled={!it.enabled}
                      onChange={(e) => updateItem(it.id, { name: e.target.value })}
                    />
                    {!it.fromAudit && <span className="gw-item-badge">Manuel</span>}
                    <input
                      type="number" min="0" step="0.1"
                      className="gw-item-vol"
                      value={it.volume}
                      disabled={!it.enabled}
                      onChange={(e) => updateItem(it.id, { volume: +e.target.value || 0 })}
                    />
                    <span className="gw-unit">m³/an</span>
                    <button type="button" className="gw-del-btn" title="Supprimer" onClick={() => removeItem(it.id)}>✕</button>
                  </div>
                ))}
                <button type="button" className="gw-add-btn" onClick={() => addItem(group)}>
                  + Ajouter un nouvel usage
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GroupedUsageTable;
